import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Shield, Building2, Mail, Phone } from "lucide-react";

const EMPTY = { name: "", contact: "", email: "", coverage_percent: 70, notes: "" };

export default function Insurance() {
  const { toast } = useToast();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/insurance/providers");
      setProviders(data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setForm(EMPTY); setEditing(null); setShowModal(true); };
  const openEdit = (p) => {
    setForm({ name: p.name, contact: p.contact || "", email: p.email || "", coverage_percent: p.coverage_percent, notes: p.notes || "" });
    setEditing(p);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, coverage_percent: parseFloat(form.coverage_percent) || 0 };
      if (editing) await api.put(`/insurance/providers/${editing.id}`, payload);
      else await api.post("/insurance/providers", payload);
      toast({ title: editing ? "Provider updated" : "Provider added" });
      setShowModal(false); fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this insurance provider?")) return;
    try {
      await api.delete(`/insurance/providers/${id}`);
      toast({ title: "Provider removed" }); fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6" data-testid="insurance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Insurance Providers</h1>
          <p className="text-slate-500 text-sm mt-1">Manage insurance partners and coverage policies</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openNew} data-testid="add-insurance-btn">
          <Plus size={16} /> Add Provider
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 rounded-xl p-4 flex items-center gap-3">
          <Shield size={22} className="text-emerald-600" />
          <div>
            <p className="text-2xl font-bold font-sans text-emerald-700">{providers.length}</p>
            <p className="text-xs text-slate-600">Active Providers</p>
          </div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
          <Building2 size={22} className="text-blue-600" />
          <div>
            <p className="text-2xl font-bold font-sans text-blue-700">
              {providers.length ? (providers.reduce((s, p) => s + (p.coverage_percent || 0), 0) / providers.length).toFixed(0) : 0}%
            </p>
            <p className="text-xs text-slate-600">Avg Coverage</p>
          </div>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 flex items-center gap-3">
          <Mail size={22} className="text-amber-600" />
          <div>
            <p className="text-2xl font-bold font-sans text-amber-700">
              {providers.filter(p => p.email).length}
            </p>
            <p className="text-xs text-slate-600">With Email Contact</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Provider Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Coverage</TableHead>
              <TableHead className="font-semibold text-slate-700">Contact</TableHead>
              <TableHead className="font-semibold text-slate-700">Email</TableHead>
              <TableHead className="font-semibold text-slate-700">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
            ) : providers.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                <Shield size={32} className="mx-auto mb-2 opacity-30" />
                <p>No insurance providers yet</p>
              </TableCell></TableRow>
            ) : providers.map(p => (
              <TableRow key={p.id} className="table-row-hover" data-testid={`insurance-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Shield size={14} className="text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{p.name}</p>
                      {p.notes && <p className="text-xs text-slate-500 truncate max-w-48">{p.notes}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs">{p.coverage_percent}% covered</Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {p.contact ? <div className="flex items-center gap-1.5"><Phone size={12} className="text-slate-400" />{p.contact}</div> : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell className="text-sm text-slate-600">{p.email || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(p)} data-testid={`edit-insurance-${p.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(p.id)} data-testid={`delete-insurance-${p.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-sans">{editing ? "Edit Provider" : "Add Insurance Provider"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <Label className="text-sm text-slate-700">Provider Name <span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Star Health, HDFC ERGO" className="mt-1 border-slate-200" required data-testid="insurance-name-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Coverage Percentage <span className="text-rose-500">*</span></Label>
              <Input type="number" min="0" max="100" value={form.coverage_percent} onChange={e => setForm({ ...form, coverage_percent: e.target.value })} className="mt-1 border-slate-200" required data-testid="insurance-coverage-input" />
              <p className="text-xs text-slate-500 mt-1">Percentage of bill covered by this provider</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-700">Contact</Label>
                <Input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} placeholder="1800-..." className="mt-1 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="claims@..." className="mt-1 border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Terms, limits, etc." className="mt-1 border-slate-200" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-insurance-btn">
                {saving ? "Saving..." : "Save Provider"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
